// Bridges the Monica avatar in a Committee editor iframe to UI owned by the
// parent shell. The parent owns both the persistent drawer and the tooltip: no
// z-index inside an iframe can paint above the shell's topbar.
const CommitteeMonicaLauncher = {
  mounted() {
    this.button = null
    this.tooltipOpen = false
    this.lastPresence = null
    this.usesParentTooltip = window.self !== window.parent

    this.postTooltipState = action => {
      if (!this.usesParentTooltip || !this.button) return

      const rect = this.button.getBoundingClientRect()

      window.parent.postMessage(
        {
          type: "seiva:committee:monica-tooltip",
          action,
          text: this.button.getAttribute("aria-label") || "Edit with Monica",
          rect: {
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          },
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
        },
        window.location.origin,
      )
    }

    this.showTooltip = () => {
      this.tooltipOpen = true
      this.postTooltipState("show")
    }

    this.hideTooltip = () => {
      this.tooltipOpen = false
      this.postTooltipState("hide")
    }

    this.repositionTooltip = () => {
      if (this.tooltipOpen) this.postTooltipState("show")
    }

    this.openMonica = () => {
      this.hideTooltip()

      // Standalone (no Committee shell around us): there is no parent to
      // post to, so the click would be dead. Hand it to the LiveView, which
      // opens the platform's own Monica drawer.
      if (window.self === window.parent) {
        this.pushEvent("open_editor_monica", {
          builder_mode: this.el.dataset.builderMode,
          resource_id: this.el.dataset.resourceId,
        })
        return
      }

      window.parent.postMessage(
        {
          type: "seiva:committee:open-monica",
          builderMode: this.el.dataset.builderMode,
          resourceId: this.el.dataset.resourceId,
        },
        window.location.origin,
      )
    }

    // Presence bridge. The launcher only renders while an editor is open in
    // this frame, so its mount/unmount IS the "an editor is open here" signal
    // the shell needs. Without it the shell only learns about editors that
    // have their own canonical url — the tool/function CREATE flow opens the
    // editor in place, so the shell kept floating its own Monica FAB and the
    // Support bubble over the editor's "Create tool" footer and refused to
    // open Monica (no builder mode for a catalog action).
    this.postPresence = action => {
      if (window.self === window.parent) return

      // `updated()` roda a cada patch do editor (uma tecla no nome da tool já
      // vale um), e cada post vira um `pushEvent` que RE-RENDERIZA o shell
      // inteiro. Só anuncia o que mudou.
      const signature = [action, this.el.dataset.builderMode, this.el.dataset.resourceId].join("|")

      if (this.lastPresence === signature) return
      this.lastPresence = signature

      window.parent.postMessage(
        {
          type: "seiva:committee:editor-monica",
          action,
          builderMode: this.el.dataset.builderMode,
          resourceId: this.el.dataset.resourceId,
        },
        window.location.origin,
      )
    }

    // LiveView can patch the launcher's children without remounting the hook,
    // so the bridge rebinds the button after every patch. The only visual
    // tooltip lives in the parent portal; the iframe keeps no local duplicate.
    this.syncElements = () => {
      const nextButton = this.el.querySelector("button")

      if (this.button !== nextButton) {
        this.button?.removeEventListener("click", this.openMonica)
        this.button?.removeEventListener("focus", this.showTooltip)
        this.button?.removeEventListener("blur", this.hideTooltip)
        this.button = nextButton
        this.button?.addEventListener("click", this.openMonica)
        this.button?.addEventListener("focus", this.showTooltip)
        this.button?.addEventListener("blur", this.hideTooltip)
      }
    }

    // Trocar o `src` do frame descarta este documento sem passar pelo
    // `destroyed()` do hook, e o shell ficaria achando que ainda há editor
    // aberto (Suporte escondido numa tela que não é editor). `pagehide` ainda
    // consegue postar para o pai.
    this.onPageHide = () => this.postPresence("gone")

    this.syncElements()
    this.postPresence("present")
    window.addEventListener("pagehide", this.onPageHide)
    this.el.addEventListener("mouseenter", this.showTooltip)
    this.el.addEventListener("mouseleave", this.hideTooltip)
    window.addEventListener("resize", this.repositionTooltip)
    window.addEventListener("scroll", this.repositionTooltip, true)
  },

  updated() {
    this.syncElements?.()
    this.postPresence?.("present")
    this.repositionTooltip?.()
  },

  destroyed() {
    this.hideTooltip?.()
    this.postPresence?.("gone")
    this.button?.removeEventListener("click", this.openMonica)
    this.el.removeEventListener("mouseenter", this.showTooltip)
    this.el.removeEventListener("mouseleave", this.hideTooltip)
    this.button?.removeEventListener("focus", this.showTooltip)
    this.button?.removeEventListener("blur", this.hideTooltip)
    window.removeEventListener("resize", this.repositionTooltip)
    window.removeEventListener("scroll", this.repositionTooltip, true)
    window.removeEventListener("pagehide", this.onPageHide)
  },
}

export default CommitteeMonicaLauncher
